import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FormError } from "@/components/FormField";
import { FieldInput } from "@/components/forms/FieldInput";

export function isVisible(field, answers) {
  const c = field.condition;
  if (!c) return true;
  const v = answers[c.field];
  if ("equals" in c) return v === c.equals;
  if ("in" in c) return (c.in || []).includes(v);
  return true;
}

function validateStep(step, answers) {
  const errors = {};
  step.fields.filter((f) => isVisible(f, answers)).forEach((f) => {
    const v = answers[f.key];
    let empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
    if (f.type === "acknowledgment") empty = v !== true;
    if (f.type === "signature") empty = !(v && v.typed_name?.trim() && v.consent_to_esign);
    if (f.required && empty) errors[f.key] = f.type === "acknowledgment" || f.type === "signature" ? "Please complete this before continuing." : "Please answer this question.";
  });
  return errors;
}

export const FormRunner = ({ templateKey, onSubmitted, exitHref = "/portal" }) => {
  const navigate = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/forms/templates/${templateKey}/published`), api.get(`/forms/submissions/${templateKey}/draft`)])
      .then(([t, d]) => { if (!alive) return; setTpl(t.data); if (d.data.exists) { setAnswers(d.data.answers || {}); setStepIndex(Math.min(d.data.step_index || 0, t.data.schema.steps.length - 1)); } })
      .catch((e) => setFormError(apiError(e)));
    return () => { alive = false; };
  }, [templateKey]);

  const visibleAnswers = useMemo(() => ({ ...(tpl?.context || {}), ...answers }), [tpl, answers]);
  const steps = useMemo(() => (tpl ? tpl.schema.steps.filter((s) => s.fields.some((f) => isVisible(f, visibleAnswers))) : []), [tpl, visibleAnswers]);
  const step = steps[Math.min(stepIndex, Math.max(steps.length - 1, 0))];
  const total = steps.length;
  const isLast = stepIndex >= total - 1;

  const saveDraft = async (nextIndex) => {
    try { await api.put(`/forms/submissions/${templateKey}/draft`, { answers, step_index: nextIndex }); setSavedAt(new Date()); } catch (e) { setFormError(apiError(e)); throw e; }
  };

  const next = async () => {
    const errs = validateStep(step, visibleAnswers);
    setErrors(errs);
    setFormError("");
    if (Object.keys(errs).length) return;
    setBusy(true);
    try { await saveDraft(stepIndex + 1); setStepIndex(stepIndex + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* shown */ } finally { setBusy(false); }
  };
  const back = () => { setErrors({}); setStepIndex(Math.max(0, stepIndex - 1)); window.scrollTo({ top: 0 }); };
  const saveAndExit = async () => { setBusy(true); try { await saveDraft(stepIndex); navigate(exitHref); } catch { /* shown */ } finally { setBusy(false); } };

  const submit = async () => {
    const errs = validateStep(step, visibleAnswers);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    setFormError("");
    try {
      const { data } = await api.post(`/forms/submissions/${templateKey}/submit`, { answers });
      onSubmitted(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.fields) {
        setErrors(detail.fields);
        const firstKey = Object.keys(detail.fields)[0];
        const idx = steps.findIndex((s) => s.fields.some((f) => f.key === firstKey));
        if (idx >= 0) setStepIndex(idx);
        setFormError(detail.message);
      } else setFormError(apiError(e));
    } finally { setBusy(false); }
  };

  if (formError && !tpl) return <FormError message={formError} />;
  if (!tpl || !step) return <p className="text-ink-500" role="status">Loading your form…</p>;

  return (
    <div className="max-w-xl" data-testid={`form-runner-${templateKey}`}>
      <p className="text-sm font-semibold text-ink-500" data-testid="form-progress">Step {stepIndex + 1} of {total}</p>
      <div className="mt-2 h-1 w-full bg-sage-400/40" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={stepIndex + 1} aria-label="Form progress">
        <div className="h-1 bg-olive-900 transition-[width]" style={{ width: `${((stepIndex + 1) / total) * 100}%` }} />
      </div>
      <h2 className="mt-8 text-3xl sm:text-4xl" data-testid="form-step-title">{step.title}</h2>
      {step.large_print && <p className="mt-2 text-sm font-semibold text-terracotta-600" data-testid="form-large-print-note">Shown in large print (14-point) because it is a legal authorization.</p>}
      {step.intro && <p className={`mt-3 max-w-[60ch] text-ink-900 ${step.large_print ? "text-[19px] leading-relaxed" : ""}`}>{step.intro}</p>}
      {step.who_sees && <p className="mt-4 flex items-start gap-2 text-sm text-ink-500" data-testid="form-who-sees"><Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{step.who_sees}</p>}
      <div className={`mt-8 ${step.large_print ? "large-print" : ""}`}>
        <FormError message={formError} />
        {step.fields.filter((f) => isVisible(f, visibleAnswers)).map((f) => (
          <FieldInput key={f.key} field={f} value={answers[f.key]} error={errors[f.key]} onChange={(v) => { setAnswers((a) => ({ ...a, [f.key]: v })); setErrors((e) => ({ ...e, [f.key]: undefined })); }} />
        ))}
      </div>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        {isLast
          ? <button type="button" className="btn-primary" onClick={submit} disabled={busy} data-testid="form-submit">{busy ? "Submitting…" : "Submit"}</button>
          : <button type="button" className="btn-primary" onClick={next} disabled={busy} data-testid="form-next">Continue</button>}
        {stepIndex > 0 && <button type="button" className="btn-secondary" onClick={back} disabled={busy} data-testid="form-back">Back</button>}
        <button type="button" className="btn-quiet sm:ml-auto" onClick={saveAndExit} disabled={busy} data-testid="form-save-exit">Save And Finish Later</button>
      </div>
      {savedAt && <p className="help mt-3" role="status">Saved {savedAt.toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", timeStyle: "short" })}.</p>}
    </div>
  );
};
